import { Container } from "@dinahmoe/dmafjs-visual";
import { coreEngine, nodes, store, env, time } from "@dinahmoe/dmafjs-core";

class CaseStoryContainer extends Container {
  namespace = "visual:case-story-container";

  handlers = {
    rootWidth: this.handleRootWidthChange,
    rootHeight: this.handleRootHeightChange,
    scrollValue: this.handleScrollValue
  };

  init(node, event) {
    super.init(node, event);
    node._isOpen = false;
    node._currentOverlay = null;
    node._overlayProjectCards = [];
    node._overlayRelatedButtons = [];
    node._isTransitioning = false;
    node._currentCaseId = null;
    node._currentSourceUid = null;
    node._currentContentType = null; // "case" or "about"
    node._overlayCloseButton = null;

    // Interest tracking — 5 priority levels, most recent last
    node._contactClicked = [];
    node._readMoreClicked = [];
    node._projectsOpened = [];
    node._caseStoriesOpened = [];
    node._categoriesClicked = [];
  }

  receiveMessage(node, message) {
    if (message.type === "open_case") {
      this.openCase(node, message.caseId, message.sourceUid);
    } else if (message.type === "open_about") {
      this.openAbout(node, message.sourceUid);
    } else if (message.type === "close") {
      this.closeOverlay(node);
    } else if (message.type === "category_expanded") {
      this.trackCategoryClick(node, message.categoryId);
    }
  }

  openCase(node, caseId, sourceUid) {
    const casesData = store.get("assets.data.cases_export");
    const caseData = casesData.case_stories.find(c => c.id === caseId);
    if (!caseData) return;

    if (node._currentOverlay) {
      const oldCaseId = node._currentCaseId;
      node._currentCaseId = caseId;
      this.transitionOverlay(node, { type: "case", caseData, caseId, sourceUid, oldCaseId });
      this.updateRoute(node, caseData.slug);
      return;
    }

    const template = store.get("template.visual.case_story");
    if (!template) return;

    const overlayNode = coreEngine.create({
      ...template,
      parentUid: node.uid
    }, { key: "create_case_overlay" });

    node._currentOverlay = overlayNode;
    node._currentCaseId = caseId;
    node._currentSourceUid = sourceUid;
    node._currentContentType = "case";
    node._overlayProjectCards = [];
    node._overlayRelatedButtons = [];

    this.configureCloseButton(node, overlayNode);
    this.dispatch("case_story:open");
    this.populateOverlay(node, overlayNode, caseData);
    this.trackCaseStoryOpen(node, caseData);

    if (node._pendingProjectSlug) {
      // Defer project expand until DOM has rendered the overlay content
      time.nextFrame(() => {
        const pendingProjectSlug = this.expandPendingProject(node);
        this.updateRoute(node, caseData.slug, pendingProjectSlug);
      });
    } else {
      this.updateRoute(node, caseData.slug);
    }
  }

  openAbout(node, sourceUid) {
    // Toggle behavior: if about is already open, close it
    if (node._currentOverlay && node._currentContentType === "about") {
      this.dispatch("case_story:close");
      return;
    }

    if (node._currentOverlay) {
      this.transitionOverlay(node, { type: "about", sourceUid });
      return;
    }

    const template = store.get("template.visual.case_story");
    if (!template) return;

    const overlayNode = coreEngine.create({
      ...template,
      parentUid: node.uid
    }, { key: "create_about_overlay" });

    node._currentOverlay = overlayNode;
    node._currentContentType = "about";
    node._currentCaseId = null;
    node._currentSourceUid = sourceUid;
    node._overlayProjectCards = [];
    node._overlayRelatedButtons = [];

    this.configureCloseButton(node, overlayNode);
    this.dispatch("case_story:open");
    this.populateAbout(node, overlayNode);
    this.dispatch("case_story:opened", undefined, {
      type: "about",
      sourceUid
    });
  }

  transitionOverlay(node, newContent) {
    if (node._isTransitioning) return;
    node._isTransitioning = true;

    const fadeDuration = 500;
    const previousSourceUid = node._currentSourceUid;
    const oldCaseId = newContent.oldCaseId ?? node._currentCaseId;

    // Fade out current overlay
    coreEngine.update({ uid: node._currentOverlay.uid, key: "transform", value: { transforms: [{ property: "appearance.opacity", valueStart: 1, valueEnd: 0, duration: fadeDuration, easing: "EASE_IN" }] } });

    setTimeout(() => {
      // Destroy old overlay nodes — use destroyOverlayNodes which guards against null
      this.destroyOverlayNodes(node);

      // Create new overlay
      const template = store.get("template.visual.case_story");
      const overlayNode = coreEngine.create({
        ...template,
        parentUid: node.uid
      }, { key: newContent.type === "case" ? "create_case_overlay" : "create_about_overlay" });

      node._currentOverlay = overlayNode;
      overlayNode["appearance.opacity"] = 0;
      this.configureCloseButton(node, overlayNode);

      if (newContent.type === "case") {
        node._currentContentType = "case";
        node._currentCaseId = newContent.caseId;
        node._currentSourceUid = newContent.sourceUid;

        this.populateOverlay(node, overlayNode, newContent.caseData);
        this.trackCaseStoryOpen(node, newContent.caseData);

        // Dispatch case_changed if transitioning case to case
        if (oldCaseId && oldCaseId !== newContent.caseId) {
          this.dispatch("case_story:case_changed", undefined, {
            oldCaseId,
            caseId: newContent.caseId,
            categoryId: newContent.caseData.category_id,
            sourceUid: newContent.sourceUid
          });
        }

        this.dispatch("case_story:opened", undefined, {
          caseId: newContent.caseId,
          sourceUid: newContent.sourceUid,
          type: "case"
        });
      } else {
        node._currentContentType = "about";
        node._currentCaseId = null;
        node._currentSourceUid = newContent.sourceUid;

        this.populateAbout(node, overlayNode);

        this.dispatch("case_story:opened", undefined, {
          type: "about",
          sourceUid: newContent.sourceUid
        });
      }

      // If transitioning from a different source, notify the previous source
      if (previousSourceUid && previousSourceUid !== (newContent.sourceUid)) {
        this.dispatch("case_story:closed", undefined, { sourceUid: previousSourceUid });
      }

      // Force scroll reset — dirty the props value to bypass dedup, then set to 0
      node.props["scroll.y"] = -1;
      coreEngine.update({ uid: node.uid, key: "scroll.y", value: 0 });

      // Fade in new overlay
      coreEngine.update({ uid: overlayNode.uid, key: "transform", value: { transforms: [{ property: "appearance.opacity", valueStart: 0, valueEnd: 1, duration: 1000, delay: 100, easing: "EASE_OUT" }] } });

      node._isTransitioning = false;
    }, fadeDuration);
  }

  populateOverlay(node, overlayNode, caseData) {
    const els = overlayNode._ids;
    if (!els) return;

    const assetsPath = store.get("serve.assetsPath");

    if (els.category_name) {
      els.category_name['text.content'] = caseData.category_name || "";
    }
    if (els.headline) {
      els.headline['text.content'] = caseData.headline || "";
    }
    if (els.case_image && caseData.image) {
      els.case_image['source.url'] = `${assetsPath}image/media/${caseData.image}`;
      if (caseData.image_alt_text) els.case_image['source.alt'] = caseData.image_alt_text;
    }
    if (els.body_text) {
      els.body_text['text.content'] = this.formatContent(caseData.content) || "";
    }
    if (els.cta_text) {
      const ctaContent = this.extractCtaText(caseData.content);
      if (ctaContent) {
        els.cta_text['text.content'] = ctaContent;
      } else {
        coreEngine.destroy(els.cta_text.uid);
      }
    }

    this.createProjectCards(node, overlayNode, caseData.projects || []);
    this.createRelatedButtons(node, overlayNode, caseData.related_case_stories || []);
  }

  populateAbout(node, overlayNode) {
    const els = overlayNode._ids;
    if (!els) return;

    const casesData = store.get("assets.data.cases_export");
    const aboutData = casesData?.pages?.find(p => p.slug === "about");
    if (!aboutData) return;

    if (els.label) {
      els.label["text.content"] = "ABOUT";
    }
    if (els.category_name) {
      coreEngine.destroy(els.category_name.uid);
    }
    if (els.headline) {
      els.headline["text.content"] = aboutData.headline || "";
    }
    if (els.case_image && aboutData.image) {
      const assetsPath = store.get("serve.assetsPath");
      els.case_image["source.url"] = `${assetsPath}image/media/${aboutData.image}`;
      if (aboutData.image_alt_text) els.case_image["source.alt"] = aboutData.image_alt_text;
    }
    if (els.body_text) {
      els.body_text["text.content"] = aboutData.content
        .replace(/&#039;/g, "'")
        .replace(/&amp;/g, "&")
        .replace(/\\r\\n/g, "\n")
        .replace(/\r\n/g, "\n") || "";
    }
    if (els.cta_text) {
      coreEngine.destroy(els.cta_text.uid);
    }
    if (els.projects_header) {
      coreEngine.destroy(els.projects_header.uid);
    }
    if (els.cards_container) {
      coreEngine.destroy(els.cards_container.uid);
    }
    if (els.related_section) {
      coreEngine.destroy(els.related_section.uid);
    }

    const overlayInner = els.overlay_inner;
    if (overlayInner) {
      overlayInner["padding.bottom"] = 32;
      coreEngine.create({
        namespace: "visual:group",
        parentUid: overlayInner.uid,
        size: { width: "100%" },
        layout: { direction: "row", alignItems: "center", justifyContent: "space-between" },
        children: [
          {
            namespace: "visual:image",
            size: { width: 97, height: 98 },
            margin: { top: 4 },
            source: { imageId: "eu", alt: "European Union" }
          },
          {
            namespace: "visual:image",
            size: { width: 200, height: 98 },
            source: { imageId: "skelleftea", alt: "Skelleftea municipality" }
          }
        ]
      });
    }
  }

  createProjectCards(node, overlayNode, projects) {
    const template = store.get("template.visual.project_card");
    if (!template || !projects.length) return;

    const cardsContainer = overlayNode._ids?.cards_container;
    if (!cardsContainer) return;

    projects.forEach((project, index) => {
      const cardElementId = `project_card_${index}`;
      const cardNode = coreEngine.create({
        ...template,
        parentUid: cardsContainer.uid,
        id: cardElementId,
        'indexIds': true
      }, { key: "create_project_card" });

      const cardEls = cardNode._ids;
      if (cardEls) {
        if (cardEls.card_image && project.image) {
          const assetsPath = store.get("serve.assetsPath");
          cardEls.card_image['source.url'] = `${assetsPath}image/media/${project.image}`;
          if (project.image_alt_text) cardEls.card_image['source.alt'] = project.image_alt_text;
        }
        if (cardEls.card_headline) {
          cardEls.card_headline['text.content'] = project.headline || "";
        }
        if (cardEls.card_description) {
          cardEls.card_description['text.content'] = project.description || "";
        }
        if (cardEls.card_cta_text && project.contact_button_text) {
          cardEls.card_cta_text['text.content'] = project.contact_button_text;
        }
        if (cardEls.card_cta_button && project.contact_link) {
          const ctaDispatchConfig = cardEls.card_cta_button._dispatchConfigs?.click;
          if (ctaDispatchConfig) {
            ctaDispatchConfig.customData = { ...ctaDispatchConfig.customData, contactLink: project.contact_link };
          }
        }
        if (cardEls.card_link && project.read_more_link) {
          const dispatchConfig = cardEls.card_link._dispatchConfigs?.click;
          if (dispatchConfig) {
            dispatchConfig.customData = { ...dispatchConfig.customData, readMoreLink: project.read_more_link };
          }
        }
      }

      node._overlayProjectCards.push({
        node: cardNode,
        id: cardElementId,
        project,
        contactLink: project.contact_link || "",
        readMoreLink: project.read_more_link || ""
      });
    });
  }

  createRelatedButtons(node, overlayNode, relatedStories) {
    const template = store.get("template.visual.related_case_button");
    if (!template || !relatedStories.length) return;

    const relatedContainer = overlayNode._ids?.related_container;
    if (!relatedContainer) return;

    relatedStories.forEach((related, index) => {
      const buttonId = `related_button_${index}`;
      const buttonNode = coreEngine.create({
        ...template,
        parentUid: relatedContainer.uid,
        id: buttonId,
        'indexIds': true
      }, { key: "create_related_button" });

      const textElement = buttonNode._ids?.related_text;
      if (textElement) {
        textElement['text.content'] = related.related_headline || "";
      }

      node._overlayRelatedButtons.push({
        node: buttonNode,
        id: buttonId,
        relatedCaseId: related.related_case_id
      });
    });
  }

  toggleProjectCard(node, event) {
    if (node._ignoreNextToggle) {
      node._ignoreNextToggle = false;
      return;
    }

    const id = event.data?.id;
    if (!id) return;

    const cardInfo = node._overlayProjectCards?.find(card => card.id === id);
    if (!cardInfo) return;

    const expandNode = cardInfo.node._ids?.card_expand;
    if (!expandNode) return;

    const isExpanded = cardInfo.node._isExpanded || false;
    const arrowNode = cardInfo.node._ids?.card_arrow;

    if (isExpanded) {
      coreEngine.update({ uid: expandNode.uid, key: "transform", value: { transforms: [{ property: "size.height", valueEnd: 0, duration: 500, easing: "EASE_IN_OUT" }] } });
      if (arrowNode) arrowNode.transform.rotate = 0;
      cardInfo.node._isExpanded = false;
    } else {
      coreEngine.update({ uid: expandNode.uid, key: "transform", value: { transforms: [{ property: "size.height", valueStart: 0, valueEnd: "auto", duration: 500, easing: "EASE_IN_OUT" }] } });
      if (arrowNode) arrowNode.transform.rotate = 180;
      cardInfo.node._isExpanded = true;

      if (cardInfo.project) {
        this.trackInterest(node, "_projectsOpened", cardInfo.project);
        const casesData = store.get("assets.data.cases_export");
        const caseData = casesData.case_stories.find(c => c.id === node._currentCaseId);
        if (caseData) {
          this.updateRoute(node, caseData.slug, cardInfo.project.slug);
        }
      }
    }
  }

  handleContact(node, event) {
    let link = event.data?.contactLink;
    if (!link) return;

    const cardInfo = node._overlayProjectCards?.find(card => card.contactLink === link);
    if (cardInfo?.project) {
      this.trackInterest(node, "_contactClicked", cardInfo.project);
    }

    if (!link.startsWith("mailto:")) link = "mailto:" + link;
    node._ignoreNextToggle = true;
    window.location.href = link;
  }

  handleReadMore(node, event) {
    const link = event.data?.readMoreLink;
    if (!link) return;

    const cardInfo = node._overlayProjectCards?.find(card => card.readMoreLink === link);
    if (cardInfo?.project) {
      this.trackInterest(node, "_readMoreClicked", cardInfo.project);
    }

    node._ignoreNextToggle = true;
    window.open(link, "_blank", "noopener,noreferrer");
  }

  handleRelatedClick(node, event) {
    const id = event.data?.id;
    if (!id) return;

    const buttonInfo = node._overlayRelatedButtons?.find(btn => btn.id === id);
    if (!buttonInfo) return;

    const casesData = store.get("assets.data.cases_export");
    const caseData = casesData.case_stories.find(c => c.id === buttonInfo.relatedCaseId);
    if (!caseData) return;

    this.openCase(node, buttonInfo.relatedCaseId, node._currentSourceUid);
  }

  trackInterest(node, arrayName, project) {
    const arr = node[arrayName];
    const key = project.slug || project.headline;
    const existing = arr.findIndex(p => (p.slug || p.headline) === key);
    if (existing !== -1) {
      arr.splice(existing, 1);
    }
    arr.push(project);
    this.updateInterestSelection(node);
  }

  trackCaseStoryOpen(node, caseData) {
    const existing = node._caseStoriesOpened.findIndex(c => c.id === caseData.id);
    if (existing !== -1) {
      node._caseStoriesOpened.splice(existing, 1);
    }
    node._caseStoriesOpened.push(caseData);
    this.updateInterestSelection(node);
  }

  trackCategoryClick(node, categoryId) {
    const existing = node._categoriesClicked.indexOf(categoryId);
    if (existing !== -1) {
      node._categoriesClicked.splice(existing, 1);
    }
    node._categoriesClicked.push(categoryId);
    this.updateInterestSelection(node);
  }

  updateInterestSelection(node) {
    const selected = [];
    const seen = new Set();

    const addProject = (project) => {
      if (selected.length >= 4) return;
      const key = project.slug || project.headline;
      if (seen.has(key)) return;
      seen.add(key);
      selected.push(project);
    };

    // Level 1: contact clicked (most recent first)
    for (let i = node._contactClicked.length - 1; i >= 0; i--) {
      addProject(node._contactClicked[i]);
    }

    // Level 2: read more clicked
    for (let i = node._readMoreClicked.length - 1; i >= 0; i--) {
      addProject(node._readMoreClicked[i]);
    }

    // Level 3: projects opened/expanded
    for (let i = node._projectsOpened.length - 1; i >= 0; i--) {
      addProject(node._projectsOpened[i]);
    }

    // Level 4: case stories opened — pick random project from each
    if (selected.length < 4) {
      for (let i = node._caseStoriesOpened.length - 1; i >= 0; i--) {
        if (selected.length >= 4) break;
        const projects = (node._caseStoriesOpened[i].projects || [])
          .filter(p => !seen.has(p.slug || p.headline));
        if (projects.length) {
          addProject(projects[Math.floor(Math.random() * projects.length)]);
        }
      }
    }

    // Level 5: categories clicked — pick random project from cases in category
    if (selected.length < 4) {
      const casesData = store.get("assets.data.cases_export");
      for (let i = node._categoriesClicked.length - 1; i >= 0; i--) {
        if (selected.length >= 4) break;
        const cases = casesData.case_stories.filter(c => c.category_id === node._categoriesClicked[i]);
        const projects = cases.flatMap(c => c.projects || [])
          .filter(p => !seen.has(p.slug || p.headline));
        if (projects.length) {
          addProject(projects[Math.floor(Math.random() * projects.length)]);
        }
      }
    }

    const connectNode = nodes.get("connect_cards");
    if (connectNode) {
      connectNode.tell({ type: "projects_updated", projects: selected });
    }
  }

  destroyOverlayNodes(node) {
    // Destroy project cards
    if (node._overlayProjectCards.length) {
      node._overlayProjectCards.forEach(card => coreEngine.destroy(card.node.uid));
      node._overlayProjectCards = [];
    }

    // Destroy related buttons
    if (node._overlayRelatedButtons.length) {
      node._overlayRelatedButtons.forEach(btn => coreEngine.destroy(btn.node.uid));
      node._overlayRelatedButtons = [];
    }

    // Destroy overlay
    if (node._currentOverlay) {
      coreEngine.destroy(node._currentOverlay.uid);
      node._currentOverlay = null;
    }

    // Destroy fixed close button
    if (node._overlayCloseButton) {
      coreEngine.destroy(node._overlayCloseButton.uid);
      node._overlayCloseButton = null;
    }

    // Reset state
    node._currentCaseId = null;
    node._currentSourceUid = null;
    node._currentContentType = null;
  }

  closeOverlay(node) {
    this.dispatch("case_story:close");
  }

  formatContent(content) {
    if (!content) return "";
    let formatted = content
      .replace(/&#039;/g, "'")
      .replace(/&amp;/g, "&")
      .replace(/\\!/g, "!")
      .replace(/\\r\\n/g, "\n")
      .replace(/\r\n/g, "\n");

    const ctaPattern = /We've got.*Let us know[!]?/i;
    if (formatted.match(ctaPattern)) {
      formatted = formatted.replace(ctaPattern, "").trim();
    }
    return formatted;
  }

  extractCtaText(content) {
    if (!content) return "";
    const ctaPattern = /We've got.*Let us know[!]?/i;
    const match = content.match(ctaPattern);
    if (match) {
      return "\n" + match[0].replace(/\\!/g, "!");
    }
    return "";
  }

  setRoute(node, config, event) {
    const { routeId, params, source } = event.data || {};
    if ((routeId !== "case-story" && routeId !== "project") || !params?.slug) return;

    const casesData = store.get("assets.data.cases_export");
    const caseData = casesData.case_stories.find(c => c.slug === params.slug);
    if (!caseData || node._currentCaseId === caseData.id) return;

    // Deep link: wait for scroll to reach showcase section, then open
    if (!source || source === "init") {
      node._pendingDeepLink = {
        categoryId: caseData.category_id,
        caseId: caseData.id,
        projectSlug: params.projectSlug || null
      };
      // In reduced motion, scroll is set instantly before this method runs —
      // check if already at the showcase position and activate immediately
      this.handleScrollValue(node);
    } else {
      this.openCase(node, caseData.id, null);
    }
  }

  updateRoute(node, slug, projectSlug) {
    if (slug && projectSlug) {
      this.dispatch("se26_navigation:navigate:project", 0, { params: { slug, projectSlug }, source: "overlay" });
    } else if (slug) {
      this.dispatch("se26_navigation:navigate:case-story", 0, { params: { slug }, source: "overlay" });
    } else {
      this.dispatch("se26_navigation:navigate:showcase", 0, { source: "overlay" });
    }
  }

  expandPendingProject(node) {
    const projectSlug = node._pendingProjectSlug;
    if (!projectSlug) return null;
    node._pendingProjectSlug = null;

    const cardInfo = node._overlayProjectCards?.find(card => card.project?.slug === projectSlug);
    if (!cardInfo) return null;

    const expandNode = cardInfo.node._ids?.card_expand;
    if (!expandNode) return null;

    coreEngine.update({ uid: expandNode.uid, key: "transform", value: { transforms: [{ property: "size.height", valueStart: 0, valueEnd: "auto", duration: 500, easing: "EASE_IN_OUT" }] } });
    const arrowNode = cardInfo.node._ids?.card_arrow;
    if (arrowNode) arrowNode.transform.rotate = 180;
    cardInfo.node._isExpanded = true;

    setTimeout(() => {
      const cardElement = document.getElementById(cardInfo.node.uid);
      if (cardElement) cardElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 600);

    return projectSlug;
  }

  handleScrollValue(node) {
    // Deep link: open case when scroll reaches showcase section
    if (node._pendingDeepLink) {
      const sectionsNode = nodes.get("sections");
      const targetScrollY = sectionsNode?.showcase;
      if (targetScrollY && targetScrollY > 0 && node.scrollValue > 0) {
        const threshold = node.rootHeight / 4;
        if (Math.abs(node.scrollValue - targetScrollY) < threshold) {
          const { categoryId, caseId, projectSlug } = node._pendingDeepLink;
          node._pendingDeepLink = null;
          node._pendingProjectSlug = projectSlug;
          const showcaseNode = nodes.get("layout_showcases");
          if (showcaseNode) {
            showcaseNode.tell({ type: "deep_link_case", categoryId, caseId });
          } else {
            this.openCase(node, caseId, null);
          }
        }
      }
    }

    // Close sidebar when scrolling away from showcase section (not when opened from search)
    if (node._isOpen && node._currentSourceUid !== "search_container") {
      const sectionsNode = nodes.get("sections");
      const showcaseY = sectionsNode?.showcase;
      if (showcaseY && showcaseY > 0) {
        const threshold = node.rootHeight / 2;
        if (Math.abs(node.scrollValue - showcaseY) > threshold) {
          this.closeOverlay(node);
        }
      }
    }
  }

  configureCloseButton(node, overlayNode) {
    if (node.rootWidth >= 1000) return;

    // Destroy the template's close button (it's inside the scrollable area)
    const closeButton = overlayNode._ids?.close_button;
    if (closeButton) coreEngine.destroy(closeButton.uid);

    // Create a fixed close button as a child of root (outside scroll container)
    if (!node._overlayCloseButton) {
      node._overlayCloseButton = coreEngine.create({
        namespace: "visual:group",
        parentUid: "root",
        size: { width: 36, height: 36 },
        position: { x: -36, y: 39 },
        anchor: { x: "right" },
        events: [{ type: "click", name: "case_story:close" }],
        children: [{
          namespace: "visual:svg",
          size: { width: 36, height: 36 },
          anchor: { x: "center", y: "center" },
          source: { imageId: "icon_plus", fill: "#FFFFFF" },
          transform: { rotate: 45 }
        }]
      }, { key: "create_overlay_close_button" });
    }
  }

  state(node, config, event) {
    const parts = event.name.split(":");
    const action = parts[parts.length - 1];
    const isOverlay = node.rootWidth < 1000;

    if (action === "open") {
      node._isOpen = true;
      if (env.motion === "reduced") {
        coreEngine.update({ uid: node.uid, key: "position.x", value: 0 });
        coreEngine.update({ uid: node.uid, key: "position.y", value: 0 });
        coreEngine.update({ uid: node.uid, key: "size.width", value: node.rootWidth });
        coreEngine.update({
          uid: node.uid,
          key: "transform",
          value: { transforms: [{ property: "appearance.opacity", valueStart: 0, valueEnd: 1, duration: 400, easing: "EASE_OUT" }] }
        });
      } else if (isOverlay) {
        coreEngine.update({
          uid: node.uid,
          key: "transform",
          value: { transforms: [{ property: "position.y", valueEnd: 0, duration: 600, easing: "EASE_IN_OUT", curve: 30 }] }
        });
      } else {
        coreEngine.update({
          uid: node.uid,
          key: "transform",
          value: { transforms: [{ property: "position.x", valueEnd: 0, duration: 600, easing: "EASE_IN_OUT", curve: 30 }] }
        });
      }
    } else if (action === "close") {
      node._isOpen = false;
      const sourceUid = node._currentSourceUid;
      if (env.motion === "reduced") {
        coreEngine.update({
          uid: node.uid,
          key: "transform",
          value: { transforms: [{ property: "appearance.opacity", valueEnd: 0, duration: 400, easing: "EASE_IN" }] }
        });
        setTimeout(() => {
          this.destroyOverlayNodes(node);
          this.dispatch("case_story:closed", undefined, { sourceUid });
          this.updateRoute(node, null);
        }, 400);
      } else if (isOverlay) {
        coreEngine.update({
          uid: node.uid,
          key: "transform",
          value: { transforms: [{ property: "position.y", valueEnd: node.rootHeight, duration: 600, easing: "EASE_IN_OUT", curve: 30 }] }
        });
      } else {
        coreEngine.update({
          uid: node.uid,
          key: "transform",
          value: { transforms: [{ property: "position.x", valueEnd: 500, duration: 600, easing: "EASE_IN_OUT", curve: 30 }] }
        });
      }
      // Destroy overlay content and notify sources after slide animation completes
      if (env.motion !== "reduced") setTimeout(() => {
        this.destroyOverlayNodes(node);
        this.dispatch("case_story:closed", undefined, { sourceUid });
        this.updateRoute(node, null);
      }, 600);
    } else if (action === "card_toggle") {
      this.toggleProjectCard(node, event);
    } else if (action === "contact") {
      this.handleContact(node, event);
    } else if (action === "read_more") {
      this.handleReadMore(node, event);
    } else if (action === "related_click") {
      this.handleRelatedClick(node, event);
    }
  }

  handleRootWidthChange(node) {
    const isOverlay = node.rootWidth < 1000;

    if (env.motion === "reduced") {
      coreEngine.update({ uid: node.uid, key: "anchor.x", value: "left" });
      coreEngine.update({ uid: node.uid, key: "size.width", value: node.rootWidth });
      coreEngine.update({ uid: node.uid, key: "position.x", value: 0 });
      coreEngine.update({ uid: node.uid, key: "position.y", value: 0 });
      if (!node._isOpen) {
        coreEngine.update({ uid: node.uid, key: "appearance.opacity", value: 0 });
      }
    } else if (isOverlay) {
      coreEngine.update({ uid: node.uid, key: "size.width", value: node.rootWidth });
      coreEngine.update({ uid: node.uid, key: "position.x", value: 0 });
      if (!node._isOpen) {
        coreEngine.update({ uid: node.uid, key: "position.y", value: node.rootHeight });
      }
    } else {
      coreEngine.update({ uid: node.uid, key: "size.width", value: 500 });
      coreEngine.update({ uid: node.uid, key: "position.y", value: 0 });
      if (!node._isOpen) {
        coreEngine.update({ uid: node.uid, key: "position.x", value: 500 });
      }
    }
  }

  handleRootHeightChange(node) {
    if (env.motion === "reduced") return;
    if (node.rootWidth < 1000 && !node._isOpen) {
      coreEngine.update({ uid: node.uid, key: "position.y", value: node.rootHeight });
    }
  }
}

const caseStoryContainer = new CaseStoryContainer();
export { caseStoryContainer, CaseStoryContainer };
